import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import TekkriHeader from '../components/TekkriHeader';
import TekkriMetricCard from '../components/TekkriMetricCard';
import TrendChart from '../components/TrendChart';
import FormDialog from '../components/FormDialog';
import AlertConfigForm from '../components/AlertConfigForm';
import { programMockData, programHistoricalData } from '../data/programMockData';
import { useToast } from '../../../hooks/useToast';
import '../tekkr.css';

/**
 * TekkrProgramView Page
 * Program-level adoption monitoring across all teams
 */
const TekkrProgramView: React.FC = () => {
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [activeView, setActiveView] = useState<'overview' | 'details'>('overview');
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleConfigureAlert = () => {
    setShowAlertDialog(true);
  };

  const handleSaveAlert = () => {
    setShowAlertDialog(false);
    showToast({
      title: 'Success',
      message: 'Alert configured successfully!',
      type: 'success'
    });
  };

  const handleViewTeam = () => {
    navigate('/tekkr/project-view');
  };

  // Define metrics for trend chart
  const trendMetrics = [
    { key: 'avgChannelCompliance', label: 'Avg. Slack Channel Compliance', color: '#e91e8c' },
    { key: 'avgSlaAdherence', label: 'Avg. SLA Adherence', color: '#7c3aed' },
    { key: 'totalOverdueBacklog', label: 'Total Overdue Backlog', color: '#ef4444' }
  ];

  // Get status badge label
  const getStatusLabel = (health: string) => {
    if (health === 'green') return 'On track';
    if (health === 'yellow') return 'At risk';
    return 'Off track';
  };

  return (
    <div className="tekkr-container">
      {/* Tekkr Header */}
      <TekkriHeader
        actions={
          <>
            <button
              className="tekkr-btn tekkr-btn-secondary"
              onClick={() => showToast({
                title: 'Info',
                message: 'Add Project feature (prototype only)',
                type: 'info'
              })}
            >
              + Add Project
            </button>
            <button
              className="tekkr-btn tekkr-btn-primary"
              onClick={handleConfigureAlert}
            >
              + Configure Alert
            </button>
          </>
        }
      />

      {/* Main Content */}
      <main className="tekkr-main-no-sidebar">
        {/* Title with Program Health */}
        <div style={{ 
          marginBottom: '1.5rem', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <h1 style={{ 
            fontSize: '1.875rem', 
            fontWeight: '600', 
            color: '#ffffff',
            margin: 0
          }}>
            {programMockData.programName}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              color: '#ef4444'
            }}>
              {programMockData.aggregateMetrics.healthScore.current}%
            </span>
            <span className="tekkr-status-badge red">
              Off track
            </span>
            <div className="tekkr-metric-alert-badge">
              🔔 5 alerts created
            </div>
          </div>
        </div>

        {/* View Toggle Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1.5rem',
          borderBottom: '1px solid var(--tekkr-border)',
          paddingBottom: '0.5rem'
        }}>
          <button
            className={`tekkr-view-toggle ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveView('overview')}
          >
            Overview
          </button>
          <button
            className={`tekkr-view-toggle ${activeView === 'details' ? 'active' : ''}`}
            onClick={() => setActiveView('details')}
          >
            Details
          </button>
        </div>

        {/* Overview View */}
        {activeView === 'overview' && (
          <>
            {/* Aggregate Metrics Grid - 4 cards */}
            <div className="tekkr-metrics-grid-compact">
              <TekkriMetricCard
                label="Avg. Slack Channel Compliance"
                current={programMockData.aggregateMetrics.avgChannelCompliance.current}
                target={programMockData.aggregateMetrics.avgChannelCompliance.target}
                unit="%"
                trend={programMockData.aggregateMetrics.avgChannelCompliance.trend}
                isHigherBetter={true}
              />
              <TekkriMetricCard
                label="Avg. SLA Adherence"
                current={programMockData.aggregateMetrics.avgSlaAdherence.current}
                target={programMockData.aggregateMetrics.avgSlaAdherence.target}
                unit="%"
                trend={programMockData.aggregateMetrics.avgSlaAdherence.trend}
                isHigherBetter={true}
              />
              <TekkriMetricCard
                label="Total Overdue Backlog"
                current={programMockData.aggregateMetrics.totalOverdueBacklog.current}
                target={programMockData.aggregateMetrics.totalOverdueBacklog.target}
                unit=" tickets"
                trend={programMockData.aggregateMetrics.totalOverdueBacklog.trend}
                isHigherBetter={false}
              />
              <TekkriMetricCard
                label="Projects at Risk"
                current={programMockData.aggregateMetrics.teamsAtRisk.current}
                target={0}
                unit={`/ ${programMockData.aggregateMetrics.teamsAtRisk.total}`}
                trend={[6, 6, 5, 5, 5, 5, 5]}
                isHigherBetter={false}
              />
            </div>

            {/* Trend Chart */}
            <TrendChart
              data={programHistoricalData}
              metrics={trendMetrics}
              defaultMetric="avgChannelCompliance"
              title="Program-Level 4-Week Trend"
              showAllByDefault={true}
            />
          </>
        )}

        {/* Details View */}
        {activeView === 'details' && (
          <div className="tekkr-teams-section">
            <div className="tekkr-teams-header">
              <div>
                <h3 className="tekkr-teams-title">Projects ({programMockData.teams.length})</h3>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--tekkr-text-muted)', 
                  marginTop: '0.25rem',
                  marginBottom: 0
                }}>
                  Each project represents one engineering team
                </p>
              </div>
              <div className="tekkr-teams-filters">
                <select className="tekkr-btn tekkr-btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                  <option>Show: All Projects</option>
                  <option>At Risk Only</option>
                  <option>On Track Only</option>
                </select>
                <select className="tekkr-btn tekkr-btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                  <option>Sort by: Health</option>
                  <option>Team Name</option>
                  <option>Overdue Count</option>
                </select>
              </div>
            </div>

            <table className="tekkr-teams-table">
              <thead>
                <tr>
                  <th>Project (Team)</th>
                  <th>Health</th>
                  <th>Slack Channel Compliance</th>
                  <th>SLA Adherence</th>
                  <th>Time to First Response</th>
                  <th>Overdue Backlog</th>
                  <th>7-Day Trend</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programMockData.teams.map((team) => {
                  const sparklineData = team.trend.map((value, index) => ({
                    index,
                    value
                  }));
                  const lineColor = team.health === 'green' ? '#10b981' : team.health === 'yellow' ? '#f59e0b' : '#ef4444';

                  return (
                    <tr key={team.id}>
                      <td>
                        <div 
                          className="tekkr-team-name"
                          onClick={handleViewTeam}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {team.name}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ 
                            fontWeight: '600',
                            color: team.health === 'green' ? '#22c55e' : team.health === 'yellow' ? '#eab308' : '#ef4444'
                          }}>
                            {team.healthPercentage}%
                          </span>
                          <span className={`tekkr-status-badge ${team.health}`}>
                            {getStatusLabel(team.health)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="tekkr-team-metric">{team.channelCompliance}%</div>
                      </td>
                      <td>
                        <div className="tekkr-team-metric">{team.slaAdherence}%</div>
                      </td>
                      <td>
                        <div className="tekkr-team-metric">{team.timeToFirstResponse}h</div>
                      </td>
                      <td>
                        <div className="tekkr-team-metric">{team.overdueBacklog}</div>
                      </td>
                      <td>
                        <div className="tekkr-team-sparkline">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={sparklineData}>
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke={lineColor}
                                strokeWidth={2}
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </td>
                      <td>
                        <button
                          className="tekkr-btn tekkr-btn-link"
                          onClick={handleViewTeam}
                          style={{ cursor: 'pointer' }}
                        >
                          View Details →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
       </main>

      {/* Alert Configuration Dialog */}
      {showAlertDialog && (
        <FormDialog
          title="Configure Program Alert"
          onConfirm={handleSaveAlert}
          onCancel={() => setShowAlertDialog(false)}
          confirmText="Save Alert"
        >
          <AlertConfigForm showScopeField={true} />
        </FormDialog>
      )}
    </div>
  );
};

export default TekkrProgramView;
